import { forwardRef, useId } from 'react';
import { AlertCircle, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

const baseField = cn(
  'w-full rounded-lg border bg-[var(--surface-raised)] text-[var(--text-primary)]',
  'placeholder:text-[var(--text-muted)]',
  'transition-[border-color,box-shadow,background-color] duration-150',
  'hover:border-[var(--border-strong)]',
  'focus:outline-none focus:border-accent-500 focus:ring-3 focus:ring-accent-500/15',
  'disabled:cursor-not-allowed disabled:bg-[var(--surface-sunken)] disabled:opacity-65',
);

/**
 * Membungkus label, pesan bantuan, dan pesan galat menjadi satu blok.
 *
 * Semuanya terhubung ke field lewat `aria-describedby` dan `aria-invalid`,
 * jadi pembaca layar mendengar galat bersamaan dengan nama field - bukan
 * sebagai teks lepas yang tidak jelas merujuk ke mana.
 */
export const Field = ({ id, label, hint, error, required, children, className }) => {
  const generated = useId();
  const fieldId = id ?? generated;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label htmlFor={fieldId} className="block text-sm font-semibold text-[var(--text-primary)]">
          {label}
          {required && (
            <span className="ml-0.5 text-danger-600" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      {children({
        id: fieldId,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': cn(errorId, hintId) || undefined,
        'aria-required': required || undefined,
      })}

      {hint && !error && (
        <p id={hintId} className="text-xs text-[var(--text-muted)]">
          {hint}
        </p>
      )}

      {error && (
        <p
          id={errorId}
          role="alert"
          className="flex items-start gap-1.5 text-xs font-medium text-danger-600 dark:text-danger-500"
        >
          <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
};

export const Input = forwardRef(function Input(
  { className, error, iconLeft: IconLeft, iconRight, size = 'md', ...props },
  ref,
) {
  const sizing = size === 'sm' ? 'h-9 text-sm' : 'h-10 text-sm';

  const field = (
    <input
      ref={ref}
      className={cn(
        baseField,
        sizing,
        'px-3',
        IconLeft && 'pl-9',
        iconRight && 'pr-9',
        error
          ? 'border-danger-400 focus:border-danger-500 focus:ring-danger-500/15'
          : 'border-[var(--border-default)]',
        className,
      )}
      {...props}
    />
  );

  if (!IconLeft && !iconRight) return field;

  return (
    <div className="relative">
      {IconLeft && (
        <IconLeft
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--text-muted)]"
          aria-hidden="true"
        />
      )}
      {field}
      {iconRight && (
        <div className="absolute top-1/2 right-2.5 -translate-y-1/2">{iconRight}</div>
      )}
    </div>
  );
});

export const Textarea = forwardRef(function Textarea({ className, error, rows = 3, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        baseField,
        'resize-y px-3 py-2 text-sm leading-relaxed',
        error ? 'border-danger-400' : 'border-[var(--border-default)]',
        className,
      )}
      {...props}
    />
  );
});

export const Select = forwardRef(function Select(
  { className, error, options = [], placeholder, children, ...props },
  ref,
) {
  return (
    // Pembungkus relatif menampung panahnya. Panah dirender sebagai ikon
    // dengan `currentColor`, bukan background image berwarna keras: dengan
    // begitu ia otomatis ikut berubah saat tema gelap, dan berputar halus
    // saat select difokus.
    //
    // `className` sengaja dipasang di PEMBUNGKUS, bukan di elemen select.
    // Pemanggil memakainya untuk mengatur lebar (`sm:w-44`), dan kalau lebar
    // itu menempel pada select sementara pembungkusnya tetap selebar penuh,
    // panahnya akan tampak terlepas jauh di kanan - terpisah dari kotaknya.
    <span className={cn('group/select relative block w-full', className)}>
      <select
        ref={ref}
        className={cn(
          baseField,
          'h-10 w-full cursor-pointer appearance-none px-3 pr-9 text-sm',
          error ? 'border-danger-400' : 'border-[var(--border-default)]',
        )}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
        {children}
      </select>
      <ChevronDown
        className={cn(
          'pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2',
          'text-[var(--text-muted)] transition-transform duration-200',
          'group-focus-within/select:rotate-180 group-focus-within/select:text-accent-500',
        )}
        aria-hidden="true"
      />
    </span>
  );
});

/**
 * Sakelar hidup/mati.
 *
 * ── Kenapa ada varian `kotak` ────────────────────────────────────────────
 *
 * Susunan bawaannya mendorong sakelar ke tepi kanan. Itu benar untuk satu
 * sakelar yang memenuhi lebar kartu, tetapi salah begitu beberapa sakelar
 * disusun berdampingan dalam grid: sakelar milik kolom pertama berhenti tepat
 * di sebelah kiri label kolom kedua, dan yang membaca menyangka sakelar itu
 * milik label yang ada di sampingnya.
 *
 * `kotak` memberi tiap sakelar batas yang terlihat sekaligus latar yang
 * berubah saat menyala, sehingga kepemilikannya tidak bisa disalahbaca dan
 * keadaan ketiganya terbaca sekaligus tanpa menelusuri satu per satu.
 */
export const Toggle = ({ checked, onChange, label, description, disabled, id, kotak = false }) => {
  const generated = useId();
  const toggleId = id ?? generated;

  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4',
        kotak && [
          'rounded-xl border p-3.5 transition-colors',
          checked
            ? 'border-accent-300 bg-accent-50 dark:border-accent-500/40 dark:bg-accent-500/10'
            : 'border-[var(--border-subtle)] bg-[var(--surface-sunken)]',
        ],
      )}
    >
      <div className="min-w-0">
        <label
          htmlFor={toggleId}
          className={cn(
            'block text-sm font-semibold text-[var(--text-primary)]',
            !disabled && 'cursor-pointer',
          )}
        >
          {label}
        </label>
        {description && <p className="mt-0.5 text-xs text-[var(--text-muted)]">{description}</p>}
      </div>

      <button
        id={toggleId}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className={cn(
          'relative h-6 w-11 shrink-0 rounded-full transition-colors duration-200',
          'disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'bg-brand-950 dark:bg-accent-600' : 'bg-ink-300 dark:bg-ink-700',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow-sm',
            'transition-transform duration-200 ease-[var(--ease-out-quart)]',
            checked && 'translate-x-5',
          )}
        />
      </button>
    </div>
  );
};
